import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';
import { outboxAuditService } from '../api/services';
import { OutboxAuditAnomaly, OutboxAuditReport, OutboxAuditTipoAnomalia } from '../types/types';

// ─── Config ──────────────────────────────────────────────────────────────────

const TIPO_CONFIG: Record<OutboxAuditTipoAnomalia, { label: string; color: 'error' | 'warning' | 'info' }> = {
  MISSING_OUTBOX_EVENT: { label: 'Evento mancante', color: 'error' },
  WRONG_CODCLIFOR: { label: 'CodClifor errato', color: 'warning' },
  DUPLICATE_PENDING: { label: 'Duplicato PENDING', color: 'info' },
};

function tipoChip(tipo: OutboxAuditTipoAnomalia) {
  const cfg = TIPO_CONFIG[tipo] ?? { label: tipo, color: 'error' };
  return <Chip label={cfg.label} size="small" color={cfg.color} sx={{ fontWeight: 'bold' }} />;
}

function fmt(dt?: string) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('it-IT');
}

// ─── Riga anomalia espandibile ────────────────────────────────────────────────

const AnomalyRow: React.FC<{ a: OutboxAuditAnomaly; idx: number }> = ({ a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow
        hover
        onClick={() => setOpen((v) => !v)}
        sx={{ cursor: 'pointer', bgcolor: idx % 2 === 0 ? 'transparent' : 'action.hover' }}
      >
        <TableCell sx={{ width: 32, pr: 0 }}>
          <IconButton size="small">{open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}</IconButton>
        </TableCell>
        <TableCell>{tipoChip(a.tipo)}</TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{a.codicePersonale || '—'}</TableCell>
        <TableCell>{a.negozioTurno || '—'}</TableCell>
        <TableCell>{a.data || '—'}</TableCell>
        <TableCell>{a.turno || '—'}</TableCell>
        <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem', maxWidth: 280 }}>
          {a.dettaglio || '—'}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Stack direction="row" flexWrap="wrap" gap={3}>
                {a.turnoId != null && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">ID Turno</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{a.turnoId}</Typography>
                  </Box>
                )}
                {a.outboxEventId != null && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Outbox Event ID</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{a.outboxEventId}</Typography>
                  </Box>
                )}
                {a.codCliforAtteso && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">CodClifor atteso</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'success.main' }}>{a.codCliforAtteso}</Typography>
                  </Box>
                )}
                {a.codCliforTrovato && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">CodClifor trovato</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'error.main' }}>{a.codCliforTrovato}</Typography>
                  </Box>
                )}
              </Stack>
              {a.dettaglio && (
                <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>{a.dettaglio}</Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ─── Pannello sommario ────────────────────────────────────────────────────────

const SummaryCard: React.FC<{ report: OutboxAuditReport }> = ({ report }) => {
  const nessuna = report.anomalieTotali === 0;
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" flexWrap="wrap" alignItems="center" gap={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {nessuna
            ? <CheckCircleIcon color="success" />
            : <WarningAmberIcon color="warning" />}
          <Typography variant="subtitle1" fontWeight="bold">
            {nessuna ? 'Nessuna anomalia' : `${report.anomalieTotali} anomali${report.anomalieTotali === 1 ? 'a' : 'e'}`}
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box>
          <Typography variant="caption" color="text.secondary">Turni analizzati</Typography>
          <Typography variant="body1" fontWeight="bold">{report.turniAnalizzati}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Turni skippati</Typography>
          <Typography variant="body1">{report.turniSkippati}</Typography>
        </Box>
        {report.eventiReInseriti > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary">Re-inseriti</Typography>
            <Typography variant="body1" color="success.main" fontWeight="bold">{report.eventiReInseriti}</Typography>
          </Box>
        )}
        <Divider orientation="vertical" flexItem />
        <Box>
          <Typography variant="caption" color="text.secondary">Eseguito</Typography>
          <Typography variant="body2">{fmt(report.eseguito)}</Typography>
        </Box>
        <Chip
          label={report.triggeredManually ? 'Manuale' : 'Schedulato'}
          size="small"
          color={report.triggeredManually ? 'primary' : 'default'}
          variant="outlined"
        />
        {/* conteggi per tipo */}
        {(['MISSING_OUTBOX_EVENT', 'WRONG_CODCLIFOR', 'DUPLICATE_PENDING'] as OutboxAuditTipoAnomalia[]).map((t) => {
          const count = report.anomalie?.filter((a) => a.tipo === t).length ?? 0;
          if (count === 0) return null;
          return (
            <Chip
              key={t}
              label={`${TIPO_CONFIG[t].label}: ${count}`}
              size="small"
              color={TIPO_CONFIG[t].color}
            />
          );
        })}
      </Stack>
    </Paper>
  );
};

// ─── Pagina principale ────────────────────────────────────────────────────────

const OutboxAuditPage: React.FC = () => {
  const navigate = useNavigate();
  const [report, setReport] = useState<OutboxAuditReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterTipo, setFilterTipo] = useState<OutboxAuditTipoAnomalia | 'ALL'>('ALL');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await outboxAuditService.getLastReport();
      setReport(res.report);
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setError('Nessun audit ancora eseguito. Premere "Esegui ora" per avviare il primo.');
      } else {
        setError(e?.response?.data?.message || 'Errore nel caricamento del report');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRunAudit = async () => {
    setRunning(true);
    setError(null);
    try {
      const res = await outboxAuditService.runAudit();
      setReport(res.report);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Errore durante l'esecuzione dell'audit");
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const anomalieFiltrate = (report?.anomalie ?? []).filter(
    (a) => filterTipo === 'ALL' || a.tipo === filterTipo
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={() => navigate('/dashboard')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Audit Outbox GDV
          </Typography>
          <Tooltip title="Ricarica ultimo report">
            <span>
              <IconButton onClick={load} disabled={loading || running} size="small" sx={{ mr: 1 }}>
                {loading ? <CircularProgress size={18} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={running ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
            onClick={handleRunAudit}
            disabled={running || loading}
            size="small"
          >
            Esegui ora
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {error && <Alert severity={error.startsWith('Nessun') ? 'info' : 'error'} sx={{ mb: 2 }}>{error}</Alert>}

        {report && <SummaryCard report={report} />}

        {report && report.anomalie.length > 0 && (
          <>
            {/* Filtro per tipo */}
            <Stack direction="row" gap={1} sx={{ mb: 2 }} flexWrap="wrap">
              <Chip
                label="Tutte"
                onClick={() => setFilterTipo('ALL')}
                color={filterTipo === 'ALL' ? 'primary' : 'default'}
                variant={filterTipo === 'ALL' ? 'filled' : 'outlined'}
              />
              {(['MISSING_OUTBOX_EVENT', 'WRONG_CODCLIFOR', 'DUPLICATE_PENDING'] as OutboxAuditTipoAnomalia[]).map((t) => {
                const count = report.anomalie.filter((a) => a.tipo === t).length;
                if (count === 0) return null;
                return (
                  <Chip
                    key={t}
                    label={`${TIPO_CONFIG[t].label} (${count})`}
                    onClick={() => setFilterTipo(t)}
                    color={filterTipo === t ? TIPO_CONFIG[t].color : 'default'}
                    variant={filterTipo === t ? 'filled' : 'outlined'}
                  />
                );
              })}
            </Stack>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ width: 32 }} />
                    <TableCell><strong>Tipo</strong></TableCell>
                    <TableCell><strong>Codice Personale</strong></TableCell>
                    <TableCell><strong>Negozio</strong></TableCell>
                    <TableCell><strong>Data</strong></TableCell>
                    <TableCell><strong>Turno</strong></TableCell>
                    <TableCell><strong>Dettaglio</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {anomalieFiltrate.map((a, idx) => (
                    <AnomalyRow key={`${a.turnoId}-${a.tipo}-${idx}`} a={a} idx={idx} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {anomalieFiltrate.length} anomali{anomalieFiltrate.length === 1 ? 'a' : 'e'} mostrat{anomalieFiltrate.length === 1 ? 'a' : 'e'}
              {filterTipo !== 'ALL' && ` (filtro: ${TIPO_CONFIG[filterTipo].label})`}
            </Typography>
          </>
        )}

        {report && report.anomalie.length === 0 && (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h6" color="success.main">Tutto allineato</Typography>
            <Typography variant="body2" color="text.secondary">
              Nessuna anomalia rilevata su {report.turniAnalizzati} turni analizzati.
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default OutboxAuditPage;
